import React, { useEffect, useState } from "react";
import { X, Save, Calendar, UserX, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { projectsApi, crewApi } from "@/lib/api";
import { prompt as promptDialog } from "@/lib/dialog";

/**
 * Per-day settings dialog opened from a DateBand's gear icon or via the
 * project panel.
 *
 * Currently controls:
 *   1. Onsite time (default 08:00) — used by drag-to-day-band drops and
 *      the new-task affordance to position new blocks at the actual call time.
 *   2. Crew NA (Not Available) management — mark crew members unavailable
 *      with an optional "until" date so the grid greys them out and warns on
 *      attempted assignment.
 */
export default function DaySettingsDialog({ open, dateKey, project, crew, onClose, onChanged }) {
  const [onsite, setOnsite] = useState("08:00");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && dateKey && project) {
      const existing = project.settings?.days?.[dateKey]?.onsite_time;
      setOnsite(existing || "08:00");
    }
  }, [open, dateKey, project]);

  if (!open || !dateKey || !project) return null;

  const saveOnsite = async () => {
    setBusy(true);
    try {
      const days = { ...(project.settings?.days || {}) };
      days[dateKey] = { ...(days[dateKey] || {}), onsite_time: onsite };
      const settings = { ...(project.settings || {}), days };
      await projectsApi.update(project.id, { settings });
      toast.success(`Onsite time for ${dateKey} → ${onsite}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save onsite time");
    } finally {
      setBusy(false);
    }
  };

  const markCrewNA = async (crewMember) => {
    const proposedUntil = await promptDialog({
      title: `Mark ${crewMember.name} unavailable`,
      body: `Pick the last day they're unavailable. Leave as ${dateKey} for just this single day.`,
      default: dateKey,
      type: "date",
      confirmLabel: "MARK UNAVAILABLE",
    });
    if (proposedUntil === null) return; // cancelled
    const until = (proposedUntil || dateKey).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(until)) {
      toast.error("Date must be in yyyy-mm-dd format.");
      return;
    }
    setBusy(true);
    try {
      await crewApi.update(crewMember.id, { unavailable_until: until });
      toast.success(`${crewMember.name} marked unavailable until ${until}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update crew");
    } finally {
      setBusy(false);
    }
  };

  const clearCrewNA = async (crewMember) => {
    setBusy(true);
    try {
      // Empty string clears the value server-side (Pydantic Optional[str]).
      await crewApi.update(crewMember.id, { unavailable_until: "" });
      toast.success(`${crewMember.name} marked available`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update crew");
    } finally {
      setBusy(false);
    }
  };

  // Partition crew by availability state ON this date so the dialog reads as
  // a glanceable status board.
  const naCrew = crew.filter((c) => c.unavailable_until && c.unavailable_until >= dateKey);
  const availCrew = crew.filter((c) => !c.unavailable_until || c.unavailable_until < dateKey);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="day-settings-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-bg-surface border border-line-strong shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div>
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">DAY SETTINGS</div>
            <div className="text-sm text-amber mono tabular-nums mt-0.5">{dateKey}</div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="day-settings-close">
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Onsite time */}
        <div className="p-4 border-b border-line space-y-2">
          <div className="flex items-center gap-2 mono text-2xs uppercase tracking-widest text-ink-mute">
            <Calendar className="w-3 h-3" /> ONSITE / CALL TIME
          </div>
          <div className="flex items-center gap-2">
            <input
              type="time"
              value={onsite}
              onChange={(e) => setOnsite(e.target.value)}
              className="mono text-sm bg-bg-elevated border border-line text-ink px-2 py-1 tabular-nums"
              data-testid="day-onsite-input"
            />
            <button
              onClick={saveOnsite}
              disabled={busy}
              className="btn-primary px-3 py-1 mono text-2xs uppercase tracking-widest disabled:opacity-50"
              data-testid="day-onsite-save"
            >
              <Save className="w-3 h-3 inline mr-1" /> SAVE
            </button>
          </div>
          <div className="text-2xs text-ink-mute">
            Floating tasks dropped on this day land at this time. New tasks default here.
          </div>
        </div>

        {/* Crew availability */}
        <div className="p-4 border-b border-line">
          <div className="flex items-center gap-2 mono text-2xs uppercase tracking-widest text-ink-mute mb-2">
            <UserX className="w-3 h-3" /> CREW · NOT AVAILABLE
          </div>
          {naCrew.length === 0 ? (
            <div className="text-2xs text-ink-mute italic">All crew available today.</div>
          ) : (
            <ul className="space-y-1">
              {naCrew.map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-xs">
                  <span className="text-ink truncate flex-1">{c.name}</span>
                  <span className="mono text-2xs text-amber tabular-nums">until {c.unavailable_until}</span>
                  <button
                    onClick={() => clearCrewNA(c)}
                    disabled={busy}
                    className="btn-ghost px-1.5 py-0.5"
                    title="Clear NA — mark as available again"
                    data-testid={`crew-na-clear-${c.id}`}
                  >
                    <RotateCcw className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-4 max-h-[200px] overflow-y-auto">
          <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-2">MARK CREW NA</div>
          {availCrew.length === 0 ? (
            <div className="text-2xs text-ink-mute italic">No crew on this project.</div>
          ) : (
            <ul className="grid grid-cols-2 gap-1">
              {availCrew.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => markCrewNA(c)}
                    disabled={busy}
                    className="btn-ghost w-full text-left px-2 py-1 text-xs"
                    data-testid={`crew-na-mark-${c.id}`}
                    title={`Mark ${c.name} unavailable from ${dateKey}`}
                  >
                    <span className="truncate">{c.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
